import { FiUsers, FiClock, FiMapPin, FiFolder, FiDollarSign, FiCreditCard, FiTrash2, FiCheckCircle, FiEdit2, FiRefreshCw, FiRepeat } from 'react-icons/fi';
import { TableActions, Tooltip } from '../../components';

export function createEmployeeColumns({
  onEdit,
  onTransfer,
  onFiles,
  onPointFiles,
  onBenefits,
  onBankData,
  onRequestDelete,
  onRequestActivate,
  onChangeStatus,
  onViewStations,
  showCompanyColumn = true,
  showStationsColumn = true,
} = {}) {
  const columns = [];

  columns.push({
    id: 'employee',
    header: 'Funcionário',
    width: '2.2fr',
    cellClassName: 'manager-table-cell-primary',
    render: employee => {
      const journeyName = employee.journey && employee.journey.name ? employee.journey.name : null;

      return (
        <>
          <div className="manager-table-avatar">
            <FiUsers size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {employee.name}
            </span>
            <span className="manager-table-subtitle">
              {employee.email || `ID: ${employee.id}`}
            </span>
            {journeyName && (
              <span
                className="manager-tag manager-tag-neutral manager-tag-sm"
                style={{ marginTop: '4px', width: 'fit-content' }}
              >
                <FiClock size={12} style={{ marginRight: 4 }} />
                {journeyName}
              </span>
            )}
          </div>
        </>
      );
    },
  });

  if (showCompanyColumn) {
    columns.push({
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: employee => (
        <span className="manager-table-subtitle">
          {employee.company ? (employee.company.corporate_name || employee.company.trade_name || 'Sem nome') : 'N/A'}
        </span>
      ),
    });
  }

  if (showStationsColumn) {
    columns.push({
      id: 'stations',
      header: 'Postos',
      width: '0.8fr',
      cellClassName: 'manager-table-cell',
      render: employee => {
        const stationCount = employee.serviceStations ? employee.serviceStations.length : 0;

        if (stationCount === 0) {
          return <span className="manager-table-subtitle">-</span>;
        }

        const content = (
          <>
            <FiMapPin size={12} />
            {stationCount}
          </>
        );

        if (onViewStations) {
          return (
            <div
              onClick={e => {
                e.stopPropagation();
                onViewStations(employee);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                padding: '0.25rem 0.6rem',
                backgroundColor: '#e0f2fe',
                color: '#0369a1',
                borderRadius: '1rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                minWidth: '50px',
              }}
              title="Clique para ver os postos"
            >
              {content}
            </div>
          );
        }

        return (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.25rem 0.6rem',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              minWidth: '50px',
            }}
          >
            {content}
          </div>
        );
      },
    });
  }

  columns.push(
    {
      id: 'details',
      header: 'Detalhes',
      width: '2.0fr',
      cellClassName: 'manager-table-cell',
      render: employee => {
        const typeLabel = employee.type === 'F' ? 'Freelancer' : 'Colaborador';

        const position =
          employee.position && employee.position.name
            ? employee.position.name
            : null;

        const benefits =
          employee.benefits && employee.benefits.length > 0
            ? employee.benefits.map(b => b.name).join(', ')
            : null;

        const salary = employee.salary
          ? Number(employee.salary).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })
          : null;

        return (
          <div className="manager-table-cell-text">
            <span className="manager-table-subtitle">
              {typeLabel}
              {position ? ` • ${position}` : ''}
            </span>
            <span className="manager-table-subtitle">
              {benefits ? `Benefícios: ${benefits}` : 'Sem benefício vinculado'}
            </span>
            <span className="manager-table-subtitle">
              {salary ? `Salário: ${salary}` : 'Salário não informado'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: employee => {
        const tags = [];

        const status = employee.status || (employee.is_active ? 'Ativo' : 'Inativo');
        let variant = 'neutral';

        switch (status) {
          case 'Ativo':
            variant = 'success';
            break;
          case 'Inativo':
            variant = 'danger';
            break;
          case 'Férias':
            variant = 'warning';
            break;
          case 'Afastado INSS':
            variant = 'warning';
            break;
          case 'Atestado Médico':
            variant = 'info';
            break;
          default:
            variant = 'neutral';
        }

        tags.push({
          id: 'status',
          label: status,
          variant,
        });

        if (employee.homeoffice) {
          tags.push({
            id: 'homeoffice',
            label: 'Home office',
            variant: 'info',
          });
        }

        return (
          <div className="manager-tags">
            {tags.map(tag => {
              const TagIcon = tag.icon;
              return (
                <span
                  key={tag.id}
                  className={[
                    'manager-tag',
                    tag.variant === 'success' && 'manager-tag-success',
                    tag.variant === 'warning' && 'manager-tag-warning',
                    tag.variant === 'danger' && 'manager-tag-danger',
                    tag.variant === 'info' && 'manager-tag-info',
                    tag.variant === 'neutral' && 'manager-tag-neutral',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {TagIcon && (
                    <TagIcon size={12} style={{ marginRight: 4 }} />
                  )}
                  {tag.label}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      id: 'ids',
      header: 'Identificação',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: employee => (
        <div className="manager-table-cnpj">
          <span>
            {employee.cpf
              ? `CPF: ${employee.cpf}`
              : employee.registration_number
              ? `Matrícula: ${employee.registration_number}`
              : `ID: ${employee.id}`}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: employee => (
        <TableActions>
          {onEdit && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onEdit(employee);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}

          {onTransfer && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onTransfer(employee);
              }}
            >
              <FiRepeat size={16} />
              Transferir
            </button>
          )}

          {onFiles && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onFiles(employee);
              }}
            >
              <FiFolder size={16} />
              Arquivos Gerais
            </button>
          )}

          {onPointFiles && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onPointFiles(employee);
              }}
            >
              <FiClock size={16} />
              Arquivos de Ponto
            </button>
          )}

          {onBenefits && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onBenefits(employee);
              }}
            >
              <FiDollarSign size={16} />
              Benefícios
            </button>
          )}

          {onBankData && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onBankData(employee);
              }}
            >
              <FiCreditCard size={16} />
              Dados Bancários
            </button>
          )}

          {onRequestDelete && employee.is_active && !onChangeStatus && (
            <button
              type="button"
              className="action-danger"
              onClick={e => {
                e.stopPropagation();
                onRequestDelete(employee);
              }}
            >
              <FiTrash2 size={16} />
              Inativar
            </button>
          )}

          {onRequestActivate && !employee.is_active && !onChangeStatus && (
            <button
              type="button"
              className="action-success"
              onClick={e => {
                e.stopPropagation();
                onRequestActivate(employee);
              }}
            >
              <FiCheckCircle size={16} />
              Ativar
            </button>
          )}

          {onChangeStatus && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onChangeStatus(employee);
              }}
            >
              <FiRefreshCw size={16} />
              Alterar Status
            </button>
          )}
        </TableActions>
      ),
    },
  );

  return columns;
}
